import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import toast from 'react-hot-toast'

// ── NSV IN-PLATFORM MESSAGING ─────────────────────────────────
// Per-job messaging between notary, admin, and client
// Routing logic:
//   notary  → message goes to admin
//   client  → message goes to admin
//   admin   → can reply to notary OR client (selectable)
//
// Messages save to Supabase job_messages table
// Real-time updates via Supabase channel subscription

// ── ROLE CONFIG ───────────────────────────────────────────────
const ROLE_CONFIG = {
  admin:      { label:'NSV Admin',  color:'#151715', bg:'#EBF4FF', icon:'👑' },
  notary:     { label:'Notary',     color:'#5E8A72', bg:'#EAF2EE', icon:'✍️' },
  client:     { label:'Client',     color:'#41427a', bg:'#EEF0F8', icon:'🏢' },
  individual: { label:'Customer',   color:'#4D4E8D', bg:'#F0EBF8', icon:'👤' },
}

// ── TIMESTAMP FORMATTER ───────────────────────────────────────
function formatTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  const diffMs = now - d
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffMins < 1)    return 'Just now'
  if (diffMins < 60)   return `${diffMins}m ago`
  if (diffHours < 24)  return `${diffHours}h ago`
  if (diffDays < 7)    return `${diffDays}d ago`
  return d.toLocaleDateString('en-US', { month:'short', day:'numeric' })
}

// ── UNREAD BADGE ──────────────────────────────────────────────
export function MessageBadge({ jobId, role }) {
  const { user } = useAuth()
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!jobId || !user) return
    loadUnread()
    const ch = supabase.channel(`badge-${jobId}-${role}`)
      .on('postgres_changes', { event:'INSERT', schema:'public', table:'job_messages', filter:`job_id=eq.${jobId}` },
        () => loadUnread()
      ).subscribe()
    return () => supabase.removeChannel(ch)
  }, [jobId, user])

  async function loadUnread() {
    const { count: c } = await supabase
      .from('job_messages')
      .select('*', { count:'exact', head:true })
      .eq('job_id', jobId)
      .eq('read', false)
      .neq('sender_role', role)
    setCount(c || 0)
  }

  if (!count) return null
  return (
    <div style={{
      display:        'inline-flex',
      alignItems:     'center',
      justifyContent: 'center',
      minWidth:       18,
      height:         18,
      borderRadius:   9,
      background:     '#E24B4A',
      color:          '#fff',
      fontSize:       10,
      fontWeight:     700,
      padding:        '0 5px',
    }}>{count}</div>
  )
}

// ── MAIN MESSAGING COMPONENT ──────────────────────────────────
export default function JobMessages({ jobId, jobNumber, role, recipientRole }) {
  const { user, profile } = useAuth()
  const [messages,    setMessages]    = useState([])
  const [loading,     setLoading]     = useState(true)
  const [open,        setOpen]        = useState(role !== 'admin')
  const [text,        setText]        = useState('')
  const [sending,     setSending]     = useState(false)
  const [unread,      setUnread]      = useState(0)
  // Admin can choose who they're replying to
  const [replyTarget, setReplyTarget] = useState(recipientRole || 'notary')
  const bottomRef = useRef(null)

  useEffect(() => {
    if (!jobId || !user) return
    loadMessages()
    // Real-time subscription
    const ch = supabase.channel(`messages-${jobId}-${role}-${replyTarget}`)
      .on('postgres_changes', {
        event:  'INSERT',
        schema: 'public',
        table:  'job_messages',
        filter: `job_id=eq.${jobId}`,
      }, payload => {
        // Only show message if relevant to current thread
        const msg = payload.new
        const isRelevant = role === 'admin'
          ? (msg.sender_role === replyTarget || msg.sender_role === 'admin')
          : (msg.sender_role === role || msg.recipient_role === role)
        if (isRelevant) {
          setMessages(prev => [...prev, msg])
          if (!open && msg.sender_role !== role) {
            setUnread(u => u + 1)
            toast.success(`New message on job ${jobNumber}!!`, { duration:4000 })
          }
        }
      }).subscribe()
    return () => supabase.removeChannel(ch)
  }, [jobId, user, replyTarget])

  // Reload messages when admin switches reply target
  useEffect(() => {
    if (role === 'admin' && jobId && user) loadMessages()
  }, [replyTarget])

  // Scroll to bottom when messages change
  useEffect(() => {
    if (open && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior:'smooth' })
    }
  }, [messages, open])

  // Mark messages as read when opened
  useEffect(() => {
    if (open && jobId && role) {
      supabase.from('job_messages')
        .update({ read: true })
        .eq('job_id', jobId)
        .eq('read', false)
        .neq('sender_role', role)
        .then(() => setUnread(0))
    }
  }, [open])

  async function loadMessages() {
    setLoading(true)
    // ✅ Filter messages relevant to this conversation thread!!
    // Show messages where: (sender is me) OR (recipient is me)
    const { data } = await supabase
      .from('job_messages')
      .select('*')
      .eq('job_id', jobId)
      .or(
        role === 'admin'
          ? `sender_role.eq.${replyTarget},sender_role.eq.admin`
          : `sender_role.eq.${role},recipient_role.eq.${role}`
      )
      .order('created_at', { ascending: true })
    setMessages(data || [])
    const unreadMsgs = (data || []).filter(m => !m.read && m.sender_role !== role)
    setUnread(unreadMsgs.length)
    setLoading(false)
  }

  async function sendMessage() {
    if (!text.trim()) return
    setSending(true)
    try {
      const senderName = profile?.full_name || role
      // Determine recipient based on role
      let recipient = 'admin'
      if (role === 'admin') recipient = replyTarget
      else recipient = 'admin'

      await supabase.from('job_messages').insert({
        job_id:       jobId,
        sender_id:    user.id,
        sender_role:  role,
        sender_name:  senderName,
        recipient_role: recipient,
        message:      text.trim(),
        read:         false,
      })
      setText('')
    } catch (err) {
      toast.error('Could not send message: ' + err.message)
    } finally {
      setSending(false)
    }
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const roleConfig  = ROLE_CONFIG[role] || ROLE_CONFIG.client
  const totalMsgs   = messages.length

  return (
    <div style={{ borderTop:'1px solid rgba(77,78,141,0.2)' }}>
      {/* ── TOGGLE BUTTON ── */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width:      '100%',
          background: 'none',
          border:     'none',
          padding:    '10px 18px',
          display:    'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor:     'pointer',
          fontSize:   12,
          color:      '#151715',
          fontWeight: 500,
          fontFamily: 'inherit',
        }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span>💬 Messages</span>
          {totalMsgs > 0 && (
            <span style={{ fontSize:10, color:'#5C6065' }}>{totalMsgs} message{totalMsgs !== 1 ? 's' : ''}</span>
          )}
          {unread > 0 && (
            <div style={{
              background:  '#E24B4A',
              color:       '#fff',
              fontSize:    10,
              fontWeight:  700,
              padding:     '1px 7px',
              borderRadius:20,
            }}>{unread} new</div>
          )}
        </div>
        <span style={{ color:'#A2A6AA' }}>{open ? '▲' : '▼'}</span>
      </button>

      {/* ── MESSAGE PANEL ── */}
      {open && (
        <div style={{ padding:'0 18px 14px' }}>

          {/* Admin reply target selector */}
          {role === 'admin' && (
            <div style={{ display:'flex', gap:6, marginBottom:10 }}>
              <span style={{ fontSize:11, color:'#5C6065', alignSelf:'center' }}>Reply to:</span>
              {['notary','client'].map(r => (
                <button key={r} type="button"
                  onClick={() => setReplyTarget(r)}
                  style={{
                    fontSize:     11,
                    fontWeight:   replyTarget === r ? 600 : 400,
                    color:        replyTarget === r ? '#151715' : '#5C6065',
                    background:   replyTarget === r ? '#EBF4FF' : 'transparent',
                    border:       `1px solid ${replyTarget === r ? '#3B82F6' : '#D7DADE'}`,
                    borderRadius: 20,
                    padding:      '3px 12px',
                    cursor:       'pointer',
                    fontFamily:   'inherit',
                    transition:   'all 0.15s',
                  }}>
                  {ROLE_CONFIG[r].icon} {ROLE_CONFIG[r].label}
                </button>
              ))}
            </div>
          )}

          {/* Messages list */}
          <div style={{
            maxHeight:    240,
            overflowY:    'auto',
            marginBottom: 10,
            display:      'flex',
            flexDirection:'column',
            gap:          8,
          }}>
            {loading && <div style={{ fontSize:12, color:'#A2A6AA', textAlign:'center', padding:'12px 0' }}>Loading...</div>}

            {!loading && messages.length === 0 && (
              <div style={{ fontSize:12, color:'#A2A6AA', textAlign:'center', padding:'16px 0' }}>
                No messages yet. Start the conversation!!
              </div>
            )}

            {messages.map(msg => {
              const isMe    = msg.sender_role === role
              const sConfig = ROLE_CONFIG[msg.sender_role] || ROLE_CONFIG.client
              return (
                <div key={msg.id} style={{
                  display:   'flex',
                  flexDirection: isMe ? 'row-reverse' : 'row',
                  gap:       8,
                  alignItems:'flex-end',
                }}>
                  {/* Avatar */}
                  <div style={{
                    width:          28,
                    height:         28,
                    borderRadius:   '50%',
                    background:     isMe ? 'linear-gradient(135deg,#4D4E8D,#41427a)' : sConfig.bg,
                    border:         `1px solid ${sConfig.color}40`,
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'center',
                    fontSize:       13,
                    flexShrink:     0,
                  }}>{sConfig.icon}</div>

                  {/* Bubble */}
                  <div style={{ maxWidth:'72%' }}>
                    {!isMe && (
                      <div style={{ fontSize:9, color:'#A2A6AA', marginBottom:2, fontWeight:600, letterSpacing:'0.06em' }}>
                        {msg.sender_name || sConfig.label}
                      </div>
                    )}
                    <div style={{
                      background:   isMe ? 'linear-gradient(135deg,#4D4E8D,#41427a)' : sConfig.bg,
                      color:        isMe ? '#fff' : sConfig.color,
                      border:       isMe ? 'none' : `1px solid ${sConfig.color}30`,
                      borderRadius: isMe ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                      padding:      '8px 12px',
                      fontSize:     12,
                      lineHeight:   1.5,
                    }}>{msg.message}</div>
                    <div style={{
                      fontSize:  9,
                      color:     '#A2A6AA',
                      marginTop: 3,
                      textAlign: isMe ? 'right' : 'left',
                    }}>{formatTime(msg.created_at)}</div>
                  </div>
                </div>
              )
            })}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={handleKey}
              placeholder={`Message ${role === 'admin' ? (replyTarget === 'notary' ? 'notary' : 'client') : 'NSV Admin'}... (Enter to send)`}
              rows={2}
              style={{
                flex:         1,
                padding:      '9px 12px',
                border:       '1.5px solid #D7DADE',
                borderRadius: 10,
                fontSize:     12,
                fontFamily:   'inherit',
                resize:       'none',
                lineHeight:   1.5,
              }}
            />
            <button
              type="button"
              onClick={sendMessage}
              disabled={sending || !text.trim()}
              style={{
                background:   text.trim() ? 'linear-gradient(135deg,#4D4E8D,#41427a)' : '#E2E4E7',
                border:       'none',
                borderRadius: 10,
                padding:      '10px 16px',
                cursor:       text.trim() ? 'pointer' : 'not-allowed',
                fontSize:     18,
                transition:   'all 0.15s',
                flexShrink:   0,
              }}>
              {sending ? '⏳' : '📤'}
            </button>
          </div>
          <div style={{ fontSize:10, color:'#A2A6AA', marginTop:4 }}>
            Press Enter to send · Shift+Enter for new line
          </div>
        </div>
      )}
    </div>
  )
}
